import type { EventRecord } from '../api/types'

/**
 * Event dates, in Shanghai time.
 *
 * Fixed UTC+8 rather than Intl: the mini program's Intl support is unreliable,
 * and every table is in Shanghai anyway. Keeping the web on the same maths
 * means a dinner never shows a different hour in the two apps.
 */

const pad = (n: number) => String(n).padStart(2, '0')

export interface ShanghaiParts {
  year: number; month: number; day: number; hour: number; minute: number; weekday: number
}

export function shanghaiParts(raw?: string | null): ShanghaiParts | null {
  if (!raw) return null
  const ms = Date.parse(String(raw).replace(' ', 'T'))
  if (!Number.isFinite(ms)) return null
  const shifted = new Date(ms + 8 * 60 * 60 * 1000)
  return {
    year: shifted.getUTCFullYear(),
    month: shifted.getUTCMonth() + 1,
    day: shifted.getUTCDate(),
    hour: shifted.getUTCHours(),
    minute: shifted.getUTCMinutes(),
    weekday: shifted.getUTCDay()
  }
}

const EN_MONTH = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec']
const EN_DAY = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat']
const ZH_DAY = ['周日','周一','周二','周三','周四','周五','周六']

const dateOf = (p: ShanghaiParts, lang: string) =>
  lang === 'zh'
    ? `${p.month}月${p.day}日 ${ZH_DAY[p.weekday]}`
    : `${EN_DAY[p.weekday]}, ${EN_MONTH[p.month - 1]} ${p.day}`

const timeOf = (p: ShanghaiParts) => `${pad(p.hour)}:${pad(p.minute)}`
const sameDay = (a: ShanghaiParts, b: ShanghaiParts) =>
  a.year === b.year && a.month === b.month && a.day === b.day

export function formatEventWhen(startAt?: string | null, endAt?: string | null, lang = 'en'): string {
  const start = shanghaiParts(startAt)
  if (!start) return ''
  const end = endAt ? shanghaiParts(endAt) : null
  const base = `${dateOf(start, lang)} · ${timeOf(start)}`
  if (!end) return base
  return sameDay(start, end) ? `${base}–${timeOf(end)}` : `${base} – ${dateOf(end, lang)} ${timeOf(end)}`
}

export function formatEventMeta(event?: Partial<EventRecord> | null, lang = 'en'): string {
  if (!event) return ''
  const bits: string[] = []
  const when = formatEventWhen(event.start_at, event.end_at, lang)
  if (when) bits.push(when)
  const capacity = Number(event.capacity)
  if (Number.isFinite(capacity) && capacity > 0) {
    bits.push(lang === 'zh' ? `${capacity}人桌` : `table of ${capacity}`)
  }
  return bits.join(' · ')
}

/**
 * Has this table already happened?
 *
 * `end_at` when we have it — a table is not "past" halfway through dinner —
 * otherwise `start_at`. An event with no usable date counts as upcoming:
 * better to show something unbooked than to silently hide it.
 */
export function isPastEvent(event?: Partial<EventRecord> | null, now: number = Date.now()): boolean {
  const raw = event?.end_at || event?.start_at
  if (!raw) return false
  const at = Date.parse(String(raw).replace(' ', 'T'))
  return Number.isFinite(at) && at < now
}

/** Newest first — for past events the most recent one is the interesting one. */
export function sortByStartDesc<T extends { start_at?: string | null }>(events: T[] = []): T[] {
  return [...events].sort((a, b) => Date.parse(b.start_at || '0') - Date.parse(a.start_at || '0'))
}
